import * as THREE from 'three';
import type { GameMap } from '@sunderfen/shared';
import type { Particles } from './particles.js';

/**
 * Rain you can see coming: tall grey curtains of falling rain on the horizon, upwind, that roll
 * in over the last two minutes before the server's forecast turns to rain, hang about while it
 * rains, and drift away downwind as it clears.
 */
export class RainFront {
  private group = new THREE.Group();
  private sheets: THREE.Mesh[] = [];
  private tex: THREE.CanvasTexture;
  private dist = 140;
  private alpha = 0;
  constructor(scene: THREE.Scene) {
    const c = document.createElement('canvas'); c.width = 64; c.height = 256;
    const x = c.getContext('2d')!;
    const g = x.createLinearGradient(0, 0, 0, 256);
    g.addColorStop(0, 'rgba(255,255,255,0)'); g.addColorStop(0.15, 'rgba(255,255,255,0.75)'); g.addColorStop(0.85, 'rgba(255,255,255,0.6)'); g.addColorStop(1, 'rgba(255,255,255,0)');
    x.fillStyle = g; x.fillRect(0, 0, 64, 256);
    // streaks of falling rain
    x.globalCompositeOperation = 'destination-out';
    for (let i = 0; i < 70; i++) { x.fillStyle = `rgba(0,0,0,${0.3 + Math.random() * 0.5})`; x.fillRect(Math.random() * 64, Math.random() * 256, 1 + Math.random() * 2, 20 + Math.random() * 60); }
    x.globalCompositeOperation = 'source-over';
    for (let x0 = 0; x0 < 64; x0 += 16) { const e = x.createLinearGradient(x0, 0, x0 + 16, 0); e.addColorStop(0, 'rgba(255,255,255,0)'); e.addColorStop(0.5, 'rgba(255,255,255,0.06)'); e.addColorStop(1, 'rgba(255,255,255,0)'); x.fillStyle = e; x.fillRect(x0, 0, 16, 256); }
    this.tex = new THREE.CanvasTexture(c);
    this.tex.wrapS = THREE.RepeatWrapping;
    for (let i = 0; i < 7; i++) {
      const m = new THREE.Mesh(new THREE.PlaneGeometry(22 + Math.random() * 12, 24), new THREE.MeshBasicMaterial({ map: this.tex, color: 0x5a6470, transparent: true, opacity: 0, depthWrite: false, fog: false, side: THREE.DoubleSide }));
      m.userData.off = (i - 3) * 16 + (Math.random() - 0.5) * 6; m.userData.depth = (Math.random() - 0.5) * 14; m.userData.ph = Math.random() * 10;
      this.sheets.push(m); this.group.add(m);
    }
    scene.add(this.group);
  }
  /**
   * `approach` 0..1: how close the rain is (1 = here); `leaving` true once it's raining and the
   * forecast says it will clear. `wind` is the direction weather blows toward.
   */
  update(dt: number, time: number, cam: THREE.Camera, tx: number, tz: number, approach: number, raining: boolean, leaving: number, wind: THREE.Vector2, sky: THREE.Color) {
    // raining: curtains stand all round at middle distance; coming: they close in from upwind; leaving: they go downwind
    const wantDist = raining ? 18 + leaving * 60 : 75 - approach * 63;
    const wantAlpha = raining ? 0.5 * (1 - leaving * 0.6) : approach * 0.55;
    this.dist += (wantDist - this.dist) * Math.min(1, dt * 1.2);
    this.alpha += (wantAlpha - this.alpha) * Math.min(1, dt * 0.8);
    this.group.visible = this.alpha > 0.01;
    if (!this.group.visible) return;
    const side = raining && !leaving ? 1 : leaving ? -1 : 1;
    const ux = -wind.x * side, uz = -wind.y * side, px = -uz, pz = ux;
    this.tex.offset.y = -time * 1.6;
    for (const m of this.sheets) {
      const off = m.userData.off as number, d = this.dist + (m.userData.depth as number);
      m.position.set(tx + ux * d + px * off, 9, tz + uz * d + pz * off);
      m.lookAt(cam.position.x, 9, cam.position.z);
      const mm = m.material as THREE.MeshBasicMaterial;
      mm.opacity = this.alpha * (0.75 + 0.25 * Math.sin(time * 0.3 + (m.userData.ph as number)));
      // storm grey, blending into the sky the further off it is
      mm.color.setRGB(0.32, 0.36, 0.42).lerp(sky, Math.min(0.6, d / 220));
    }
  }
}

/** Snowfall on the high ground, and the snow line that whitens it. */
export const SNOW_REGIONS = ['ashen', 'rustcliff', 'copperback'];
/** Height (world units) above which snow lies: the peaks and ridge crests, patchy on the highest ground. */
export const SNOW_LINE = 6.2;
/** 0..1: how snowy it is at this point of the map. */
export function snowAt(map: GameMap, x: number, z: number) {
  const tx = Math.floor(x), tz = Math.floor(z);
  if (!map.inBounds(tx, tz)) return 0;
  const key = map.regions.find(r => r.id === map.region[map.idx(tx, tz)])?.key;
  if (!key || !SNOW_REGIONS.includes(key)) return 0;
  const h = map.heightAt(x, z);
  return Math.max(0, Math.min(1, (h - SNOW_LINE + 0.4) / 1.0));
}

export class Snowfall {
  private clock = 0;
  constructor(private particles: Particles, private map: GameMap) {}
  update(dt: number, tx: number, tz: number, wind: THREE.Vector2, extra: number) {
    // snow falls where it's snowy near the player, harder in bad weather
    const k = snowAt(this.map, tx, tz);
    if (k <= 0.02) return;
    this.clock -= dt;
    if (this.clock > 0) return;
    this.clock = 0.05 / (0.4 + k + extra);
    for (let i = 0; i < 3; i++) {
      const x = tx + (Math.random() - 0.5) * 26, z = tz + (Math.random() - 0.5) * 26;
      const y = Math.max(this.map.heightAt(x, z), 0) + 5 + Math.random() * 4;
      this.particles.emit({ x, y, z, count: 1, spread: 0.3, vy: -0.7, vSpread: 0.2, colors: [0xffffff, 0xeef4ff], size: 0.07, life: 7, wander: 0.5, drag: 0, alpha: 0.95, gravity: 0 });
    }
    void wind;
  }
}
